import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext'; // Import AuthContext
import Notification from '../components/Notification';
import { API_BASE_URL } from '../config';
import './CoverArtEditor.css';

const BASE_S3_URL = "https://tdj-s3.s3.eu-west-2.amazonaws.com/";
const TEMPLATES = {
  track: "TRACK_IMAGE_TEMPLATE.jpg",
  artist: "ARTIST_IMAGE_TEMPLATE.jpg",
  playlist: "PLAYLIST_IMAGE_TEMPLATE.jpg",
};

const CoverArtEditor = ({ resourceId, onUploadComplete, extension = 'jpg', template = 'playlist' }) => {
  const { authData } = useContext(AuthContext); // Access authData
  const fallbackImageUrl = `${BASE_S3_URL}${TEMPLATES[template] || TEMPLATES.playlist}`;
  const uploadUrl = `${API_BASE_URL}/api/storage/uploadFile/`;

  const [previewImage, setPreviewImage] = useState(`${BASE_S3_URL}${resourceId}.${extension}`);
  const [isUploading, setIsUploading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [cacheBuster, setCacheBuster] = useState(Date.now()); // Cache-busting parameter

  // Update the previewImage whenever resourceId changes
  useEffect(() => {
    setPreviewImage(`${BASE_S3_URL}${resourceId}.${extension}?cb=${cacheBuster}`);
  }, [resourceId, extension, cacheBuster]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);

    // Automatically remove notification after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((notification) => notification.id !== id));
    }, 5000);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Preview the image
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target.result); // Show a preview of the selected image
      };
      reader.readAsDataURL(file);

      // Start the upload process
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file, `${resourceId}.${extension}`); // Rename file to `id.extension`

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`, // Attach authorization token
          },
          body: formData, // Send the file using FormData
        });

        if (!response.ok) {
          throw new Error('Failed to upload file');
        }

        const data = await response;
        setIsUploading(false);
        addNotification('Cover art uploaded successfully', 'success');

        // Add a small delay before updating the cache buster
        setTimeout(() => {
          setCacheBuster(Date.now()); // Update the cache buster after a delay
        }, 1000);

        if (onUploadComplete) {
          onUploadComplete(data.url); // Pass the uploaded file URL back
        }
      } catch (error) {
        setIsUploading(false);
        addNotification('Failed to upload cover art', 'error');
      }
    }
  };

  return (
    <div className="cover-art-editor">
      <img 
        src={previewImage} 
        alt="Cover Art" 
        className="cover-art" 
        onError={(e) => { 
          e.target.onerror = null; // Prevent infinite loop in case fallback fails
          e.target.src = fallbackImageUrl; 
        }} 
      />
      <div className="edit-link-container">
        <label htmlFor={`cover-art-input-${resourceId}`} className="edit-link">
          {isUploading ? 'Uploading...' : 'Edit'}
        </label>
        <input
          type="file"
          id={`cover-art-input-${resourceId}`}
          accept={`image/${extension === 'jpg' ? 'jpeg' : extension}`}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={isUploading}
        />
      </div>

      {/* Render Notifications */}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() =>
              setNotifications((prev) =>
                prev.filter((n) => n.id !== notification.id)
              )
            }
          />
        ))}
      </div>
    </div>
  );
};

export default CoverArtEditor;
